import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  getAuthHeaders, 
  handleApiError, 
  formatPhoneNumber, 
  makeApiCall 
} from '../utils/customerUtils'
import { Partner } from './PartnerForm'

const PAGE_SIZE_DEFAULT = 25

interface PartnerListProps {
  onSelectPartner?: (partner: Partner) => void
  onEditPartner?: (partner: Partner) => void
  onDeletePartner?: (partner: Partner) => void
  showActions?: boolean
  maxPartners?: number
}

export function PartnerList({ 
  onSelectPartner, 
  onEditPartner, 
  onDeletePartner, 
  showActions = true,
  maxPartners
}: PartnerListProps) {
  const { t } = useTranslation()
  const [partners, setPartners] = useState<Partner[]>([])
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<Set<number>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    fetchPartners()
  }, [])

  useEffect(() => {
    // Apply search filter
    if (!searchTerm.trim()) {
      setFilteredPartners(partners)
    } else {
      const search = searchTerm.toLowerCase()
      const filtered = partners.filter(partner => 
        partner.name.toLowerCase().includes(search) ||
        partner.email?.toLowerCase().includes(search) ||
        partner.phone?.toLowerCase().includes(search) ||
        partner.agency?.toLowerCase().includes(search)
      )
      setFilteredPartners(filtered)
    }
    setCurrentPage(1)
  }, [partners, searchTerm])

  const fetchPartners = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await makeApiCall('api/partners')

      if (!response.ok) {
        throw new Error('Failed to fetch partners')
      }

      const data = await response.json()
      setPartners(data.partners || [])
    } catch (err) {
      console.error('Error fetching partners:', err)
      setError(handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleDeletePartner = async (partner: Partner) => {
    if (!window.confirm(`Are you sure you want to delete partner "${partner.name}"? This action cannot be undone.`)) {
      return
    }

    setDeleting(prev => new Set(prev).add(partner.id))

    try {
      const response = await makeApiCall(`api/partners/${partner.id}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error('Failed to delete partner')
      }

      // Remove partner from local state
      setPartners(prev => prev.filter(p => p.id !== partner.id))

      if (onDeletePartner) {
        onDeletePartner(partner)
      }
    } catch (err) {
      console.error('Error deleting partner:', err)
      setError(handleApiError(err))
    } finally {
      setDeleting(prev => {
        const newSet = new Set(prev)
        newSet.delete(partner.id)
        return newSet
      })
    }
  }

  const copyToClipboard = async (text: string, type: 'email' | 'phone') => {
    try {
      await navigator.clipboard.writeText(text)
      // You could add a toast notification here if desired
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formatPhoneForCopy = (phone: string): string => {
    // Remove all non-digit characters to get just the 10 digits
    return phone.replace(/\D/g, '')
  }

  const getPartnerInitials = (partner: Partner): string => {
    return partner.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">{t('partners.table.loadingPartners')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button 
            onClick={fetchPartners}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            {t('partners.table.retry')}
          </button>
        </div>
      </div>
    )
  }

  const totalPages = showAll ? 1 : Math.ceil(filteredPartners.length / PAGE_SIZE_DEFAULT)
  const paginatedPartners = showAll
    ? filteredPartners
    : filteredPartners.slice((currentPage - 1) * PAGE_SIZE_DEFAULT, currentPage * PAGE_SIZE_DEFAULT)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="space-y-4">
      {/* Partner Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Search Bar */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 max-w-md">
              <input
                type="text"
                placeholder={t('partners.table.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
              />
            </div>
            {filteredPartners.length > 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {showAll
                  ? `${filteredPartners.length} partners`
                  : `${Math.min((currentPage - 1) * PAGE_SIZE_DEFAULT + 1, filteredPartners.length)}–${Math.min(currentPage * PAGE_SIZE_DEFAULT, filteredPartners.length)} of ${filteredPartners.length}`
                }
              </div>
            )}
          </div>
        </div>

        {filteredPartners.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            {searchTerm
              ? t('partners.table.noPartnersMatching', { search: searchTerm })
              : t('partners.table.noPartnersFound')
            }
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('partners.table.name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('partners.table.email')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('partners.table.phone')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('partners.table.agency')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedPartners.map((partner) => (
                  <tr
                    key={partner.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${onSelectPartner ? 'cursor-pointer' : ''}`}
                    onClick={() => onSelectPartner && onSelectPartner(partner)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-9 h-9 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-emerald-700 dark:text-emerald-300 text-sm font-semibold">{getPartnerInitials(partner)}</span>
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{partner.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {partner.email ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{partner.email}</span>
                          <button onClick={(e) => { e.stopPropagation(); copyToClipboard(partner.email, 'email') }} className="text-gray-300 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Copy email">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                          </button>
                        </div>
                      ) : <span className="text-gray-400 dark:text-gray-500 text-sm">{t('partners.table.noEmail')}</span>}
                    </td>
                    <td className="px-6 py-4">
                      {partner.phone ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{formatPhoneNumber(partner.phone)}</span>
                          <button onClick={(e) => { e.stopPropagation(); copyToClipboard(formatPhoneForCopy(partner.phone), 'phone') }} className="text-gray-300 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Copy phone">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                          </button>
                        </div>
                      ) : <span className="text-gray-400 dark:text-gray-500 text-sm">{t('partners.table.noPhone')}</span>}
                    </td>
                    <td className="px-6 py-4">
                      {partner.agency
                        ? <span className="text-sm text-gray-900 dark:text-gray-100">{partner.agency}</span>
                        : <span className="text-gray-400 dark:text-gray-500 text-sm">{t('partners.table.noAgency')}</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer */}
        {!maxPartners && filteredPartners.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <span>
                {showAll
                  ? `All ${filteredPartners.length} partners`
                  : `Page ${currentPage} of ${totalPages} · ${filteredPartners.length} partners`
                }
              </span>
              <button
                onClick={() => { setShowAll(v => !v); setCurrentPage(1) }}
                className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
              >
                {showAll ? `Show ${PAGE_SIZE_DEFAULT} per page` : 'Show all'}
              </button>
            </div>

            {!showAll && totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">«</button>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page: number
                  if (totalPages <= 5) page = i + 1
                  else if (currentPage <= 3) page = i + 1
                  else if (currentPage >= totalPages - 2) page = totalPages - 4 + i
                  else page = currentPage - 2 + i
                  return (
                    <button key={page} onClick={() => handlePageChange(page)} className={`px-2.5 py-1 text-xs rounded border transition-colors ${currentPage === page ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border-gray-900 dark:border-gray-100 font-medium' : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>{page}</button>
                  )
                })}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">›</button>
                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">»</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Load More for limited views */}
      {maxPartners && partners.length > maxPartners && (
        <div className="text-center">
          <a href="/partners" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
            {t('partners.table.viewAll', { count: partners.length })}
          </a>
        </div>
      )}
    </div>
  )
}

